import { err, ok, Result } from '@easeltv/fail';

import { ConsoleDriver, DriverConfig, SentryDriver } from './drivers';
import { isDriverConfig } from './helpers/isDriverConfig';
import { DriverI, LOG_LEVEL_RANK, LogFn, LogLevel } from './types';

/**
 * Logger
 *
 * The Logger class that is called when methods like `log` or `error` are called using this package.
 * It holds the all the instantiated logging providers and map over and fire for each of them when a method is called
 *
 * @param defaultLogLevel - Log level under which logs won't be reported
 * @returns
 */
export const LoggerInstance = (defaultLogLevel: LogLevel) => {
    /** Any methods called below this level will be dropped and not reported */
    let minLogLevel = defaultLogLevel;

    /** An array of logging providers that will be called with each log method */
    const providers: DriverI[] = [];

    /**
     * registerProvider
     *
     * Instantiates and stores instances of logging providers to be called when log methods are used
     *
     * @param config - Union type of the different config formats needed to instantiate a logging provider.
     */
    const registerProvider = (config: DriverConfig): Result<string> => {
        // Check if the provided config conforms to the expected shape
        if (!isDriverConfig(config))
            // If not return failed result
            return err({
                key: 'logger.config.shape',
                userMessage: 'Config format is not the right shape',
                data: { config },
            });

        // Add provider of the config type to the providers array
        switch (config.type) {
            case 'console': {
                providers.push(ConsoleDriver(config));
                return ok('Success');
            }
            case 'sentry': {
                providers.push(SentryDriver(config));

                return ok('Success');
            }
            default:
                // Not actually reachable as the type guard should catch anything that doesn't have a case
                return err({
                    key: 'logger.config.shape',
                    userMessage: 'Config format is not the right shape',
                    data: { config },
                });
        }
    };

    /**
     * setMinLogLevel
     *
     * Update the Logger instance's minimum log level
     * Any attempts to log using methods below the minLogLevel will be dropped
     *
     * @param minLogLevel - Log level under which logs won't be reported
     */
    const setMinLogLevel = (updatedLogLevel: LogLevel) => {
        minLogLevel = updatedLogLevel;
    };

    const shouldEmit = (method: LogLevel) =>
        LOG_LEVEL_RANK[method] >= LOG_LEVEL_RANK[minLogLevel];

    /**
     * debug
     *
     * Triggers a debug level log on all registered providers
     */
    const debug: LogFn = (...args) => {
        if (shouldEmit('debug')) {
            providers.map((provider) => provider.debug(...args));
        }
    };

    /**
     * info
     *
     * Triggers a info level log on all registered providers
     */
    const info: LogFn = (...args) => {
        if (shouldEmit('info')) {
            providers.map((provider) => provider.info(...args));
        }
    };

    /**
     * log
     *
     * Triggers a log level log on all registered providers
     */
    const log: LogFn = (...args) => {
        if (shouldEmit('log')) {
            providers.map((provider) => provider.log(...args));
        }
    };

    /**
     * warn
     *
     * Triggers a warn level log on all registered providers
     */
    const warn: LogFn = (...args) => {
        if (shouldEmit('warn')) {
            providers.map((provider) => provider.warn(...args));
        }
    };

    /**
     * error
     *
     * Triggers a error level log on all registered providers
     */
    const error: LogFn = (...args) => {
        if (shouldEmit('error')) {
            providers.map((provider) => provider.error(...args));
        }
    };

    return {
        debug,
        error,
        info,
        log,
        registerProvider,
        setMinLogLevel,
        warn,
    };
};

export type LoggerInstance = ReturnType<typeof LoggerInstance>;
